/**
 * HM-397+HM-398 cluster 17 Planning Gantt: griglia camere x tempo.
 *
 * - Sticky-left col 110px room labels (codice + tipologia + piano + pallino status).
 * - Sticky-top header: 24 ore (60px) in day mode, 7 giorni (140px) in week mode.
 * - Body: per ogni camera 1 row di slots vuoti clickable + bookings absolute.
 * - Now-line: linea arancia verticale alla posizione ora corrente (day mode).
 * - BookingBlock: gradient bg per mode (dayuse/overnight) + border-left 4px per
 *   status derivato (paid/extra/late/anonymous).
 */
import { useEffect, useState } from 'react';
import type { PlanningItem } from '../../api/planning.js';
import type { RoomDb } from '../../api/rooms.js';

const ITALIAN_DOW_SHORT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

// Fixed slot arrays: 24 ore e 7 giorni. Estratte fuori dal render per
// evitare il warning noArrayIndexKey: la chiave e' il valore stringa stesso.
const HOURS_24 = Array.from({ length: 24 }, (_, i) => ({ key: `h${i}`, h: i }));
const DAYS_7 = Array.from({ length: 7 }, (_, i) => ({ key: `d${i}`, d: i }));

type ViewMode = 'day' | 'week';

export function GanttGrid({
  rooms,
  bookings,
  viewMode,
  currentDate,
  onSlotClick,
  onBookingClick,
  onBookingDrop,
}: {
  rooms: RoomDb[];
  bookings: PlanningItem[];
  viewMode: ViewMode;
  currentDate: Date;
  onSlotClick: (roomCode: string, indexInRow: number) => void;
  onBookingClick: (bookingId: string) => void;
  onBookingDrop?: (bookingId: string, targetRoomId: string, slotIndex: number) => void;
}) {
  // Now position aggiornata ogni 60s (solo day mode).
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const isToday = (() => {
    const today = new Date();
    return (
      today.getFullYear() === currentDate.getFullYear() &&
      today.getMonth() === currentDate.getMonth() &&
      today.getDate() === currentDate.getDate()
    );
  })();
  const nowHour = now.getHours() + now.getMinutes() / 60;

  return (
    <div className="planning-gantt-wrapper" data-testid="planning-gantt">
      <div className="planning-gantt">
        {/* Header */}
        <div className="planning-gantt-rooms-header">Camera</div>
        <HoursHeader
          viewMode={viewMode}
          currentDate={currentDate}
          nowHour={nowHour}
          isToday={isToday}
        />
        {/* Rows */}
        {rooms.map((room) => (
          <RoomRow
            key={room.id}
            room={room}
            bookings={bookings.filter((b) => b.roomId === room.id)}
            viewMode={viewMode}
            currentDate={currentDate}
            nowHour={nowHour}
            isToday={isToday}
            onSlotClick={(idx) => onSlotClick(room.code, idx)}
            onBookingClick={onBookingClick}
            onSlotDrop={
              onBookingDrop ? (bookingId, idx) => onBookingDrop(bookingId, room.id, idx) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function HoursHeader({
  viewMode,
  currentDate,
  nowHour,
  isToday,
}: {
  viewMode: ViewMode;
  currentDate: Date;
  nowHour: number;
  isToday: boolean;
}) {
  if (viewMode === 'day') {
    return (
      <div className="planning-gantt-hours-header">
        {HOURS_24.map(({ key, h }) => {
          const isNow = isToday && Math.floor(nowHour) === h;
          return (
            <div
              key={key}
              className={`planning-gantt-hour${isNow ? ' now' : ''}`}
              data-testid={`hour-${h}`}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          );
        })}
      </div>
    );
  }
  // Week mode: 7 giorni dal lunedi
  const weekStart = new Date(currentDate);
  const dow = (weekStart.getDay() + 6) % 7;
  weekStart.setDate(weekStart.getDate() - dow);
  const today = new Date();
  return (
    <div className="planning-gantt-hours-header week-view">
      {DAYS_7.map(({ key, d: i }) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        const isNow =
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate();
        return (
          <div
            key={key}
            className={`planning-gantt-hour week-day${isNow ? ' now' : ''}`}
            data-testid={`day-${i}`}
          >
            {ITALIAN_DOW_SHORT[d.getDay()]} {d.getDate()}
          </div>
        );
      })}
    </div>
  );
}

function RoomRow({
  room,
  bookings,
  viewMode,
  currentDate,
  nowHour,
  isToday,
  onSlotClick,
  onBookingClick,
  onSlotDrop,
}: {
  room: RoomDb;
  bookings: PlanningItem[];
  viewMode: ViewMode;
  currentDate: Date;
  nowHour: number;
  isToday: boolean;
  onSlotClick: (idx: number) => void;
  onBookingClick: (bookingId: string) => void;
  onSlotDrop?: (bookingId: string, idx: number) => void;
}) {
  // weekStart per riposizionare i bookings in week mode.
  const weekStart = (() => {
    if (viewMode === 'day') return null;
    const d = new Date(currentDate);
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  return (
    <>
      <div className="planning-gantt-room">
        <div>
          <span className={`room-status ${room.status}`} />
          <strong>{room.code}</strong>
        </div>
        <div className="room-meta">Piano {room.floor}</div>
      </div>
      <div
        className={`planning-gantt-row${viewMode === 'week' ? ' week-view' : ''}`}
        data-testid={`row-${room.code}`}
      >
        {(viewMode === 'day' ? HOURS_24 : DAYS_7).map((slot) => {
          const i = 'h' in slot ? slot.h : slot.d;
          const isNowCol = viewMode === 'day' && isToday && Math.floor(nowHour) === i;
          const isWeekend = viewMode === 'week' && (i === 5 || i === 6);
          const cls = `planning-gantt-slot${isNowCol ? ' now-col' : ''}${
            isWeekend ? ' weekend' : ''
          }`;
          return (
            <button
              type="button"
              key={`${room.code}-${slot.key}`}
              className={cls}
              onClick={() => onSlotClick(i)}
              onDragOver={
                onSlotDrop
                  ? (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  : undefined
              }
              onDrop={
                onSlotDrop
                  ? (e) => {
                      e.preventDefault();
                      const bookingId = e.dataTransfer.getData('text/plain');
                      if (bookingId) onSlotDrop(bookingId, i);
                    }
                  : undefined
              }
              aria-label={
                viewMode === 'day'
                  ? `Camera ${room.code} ore ${String(i).padStart(2, '0')}:00`
                  : `Camera ${room.code} giorno ${i + 1}`
              }
              data-testid={`slot-${room.code}-${i}`}
            />
          );
        })}
        {bookings.map((b) => (
          <BookingBlock
            key={b.id}
            booking={b}
            viewMode={viewMode}
            currentDate={currentDate}
            weekStart={weekStart}
            onClick={() => onBookingClick(b.id)}
          />
        ))}
        {viewMode === 'day' && isToday && (
          <div
            className="planning-now-line"
            style={{ left: `${(nowHour / 24) * 100}%` }}
            data-testid="now-line"
          />
        )}
      </div>
    </>
  );
}

// ----------------------------- BOOKING BLOCK (HM-398) -----------------------------

function BookingBlock({
  booking,
  viewMode,
  currentDate,
  weekStart,
  onClick,
}: {
  booking: PlanningItem;
  viewMode: ViewMode;
  currentDate: Date;
  weekStart: Date | null;
  onClick: () => void;
}) {
  const start = new Date(booking.startAt);
  const end = new Date(booking.endAt);
  const now = new Date();
  // HM-403 cluster 17.1 polish: late checkouts (status checked_in con endAt
  // scaduto) vanno renderizzati come se fossero ancora "vivi" fino alla
  // now-line. Espandiamo l'endAt effettivo a now() in modo che il blocco
  // appaia anche nella vista del giorno corrente (dove l'orario di check-out
  // sarebbe altrimenti fuori range).
  const isLate = booking.status === 'checked_in' && end.getTime() < now.getTime();
  const effectiveEnd = isLate ? now : end;
  const mode = booking.service === 'day_use' ? 'dayuse' : 'overnight';
  const statusClass = deriveStatusClass(booking, now);

  // Position:
  //  - day: left = (startHour/24)*100%, width = ((endHour-startHour)/24)*100%
  //  - week: left/width per giorno indice (0-6 dal weekStart)
  let leftPct = 0;
  let widthPct = 100;
  if (viewMode === 'day') {
    const dayStart = new Date(currentDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEndMs = dayStart.getTime() + 24 * 3600 * 1000;
    const fromMs = Math.max(start.getTime(), dayStart.getTime());
    const toMs = Math.min(effectiveEnd.getTime(), dayEndMs);
    if (toMs <= fromMs) return null;
    leftPct = ((fromMs - dayStart.getTime()) / (24 * 3600 * 1000)) * 100;
    widthPct = ((toMs - fromMs) / (24 * 3600 * 1000)) * 100;
  } else if (weekStart) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const fromMs = Math.max(start.getTime(), weekStart.getTime());
    const toMs = Math.min(effectiveEnd.getTime(), weekEnd.getTime());
    if (toMs <= fromMs) return null;
    leftPct = ((fromMs - weekStart.getTime()) / (7 * 24 * 3600 * 1000)) * 100;
    widthPct = ((toMs - fromMs) / (7 * 24 * 3600 * 1000)) * 100;
  }

  // Display: per booking molto stretti, mostra solo nome.
  const durationH = Math.round(((end.getTime() - start.getTime()) / 3600_000) * 10) / 10;
  const price = Number(booking.totalAmount).toFixed(0);
  const meta = `${durationH}h · €${price}`;
  const name = booking.guestName ?? `Walk-in ${booking.publicCode}`;

  return (
    <button
      type="button"
      className={`planning-booking ${mode} status-${statusClass}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      onClick={onClick}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', booking.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      data-testid={`booking-${booking.id}`}
      data-bookingid={booking.id}
      title={`${name} · ${meta}`}
    >
      <div className="planning-booking-name">{name}</div>
      <div className="planning-booking-meta">{meta}</div>
    </button>
  );
}

function deriveStatusClass(b: PlanningItem, now: Date): 'paid' | 'extra' | 'late' | 'anonymous' {
  // late: end_at < now AND status = checked_in (overstay)
  if (b.status === 'checked_in' && new Date(b.endAt).getTime() < now.getTime()) return 'late';
  // extra: extra_hours > 0
  if ((b.extraHours ?? 0) > 0) return 'extra';
  // anonymous: walk-in senza guest
  if (b.channel === 'walk_in' && !b.guestId) return 'anonymous';
  // paid: paidAmount >= totalAmount
  if (Number(b.paidAmount) >= Number(b.totalAmount)) return 'paid';
  return 'paid';
}
